// @flow
import * as React from 'react';

import { ChoiceField, ChoiceFieldGroup } from '@gandi/form/FinalForm';
import { selectCurrentId } from '@gandi/redux-sharing';
import { useTranslator } from '@gandi/translate';
import { Base, Block, BlockHeading } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { useFieldWithTypeValue } from 'src/hooks/useFieldWithTypeValue';
import { selectSellableMailboxOffers } from 'src/modules/mailbox/offers';

import {
  MAILBOX_DURATION_ONE_MONTH,
  MAILBOX_DURATION_INPUT_NAME,
  MAILBOX_TYPE_INPUT_NAME,
} from '../consts';
import { useDurationTranslations } from '../hooks';
import type { FormValues } from '../types/form';

import { MailboxLegacyExpiration } from './MailboxLegacyExpiration';

export function MailboxDurationChoiceGroup(): React.MixedElement | '' {
  const { __ } = useTranslator();

  const duration = useFieldWithTypeValue<
    FormValues[typeof MAILBOX_DURATION_INPUT_NAME],
  >(MAILBOX_DURATION_INPUT_NAME);
  const mailboxType = useFieldWithTypeValue<
    FormValues[typeof MAILBOX_TYPE_INPUT_NAME],
  >(MAILBOX_TYPE_INPUT_NAME);

  const { toLabel } = useDurationTranslations();
  const sharingId = useAppSelector(selectCurrentId);

  const offers = useAppSelector(state =>
    selectSellableMailboxOffers(state, sharingId),
  );
  const durationConfig = offers.find(
    offer => offer.mailbox_type === mailboxType,
  )?.duration;

  return (
    mailboxType &&
    (durationConfig ? (
      <Block>
        <BlockHeading>{__('Choose your type of renewal')}</BlockHeading>
        <ChoiceFieldGroup
          name={MAILBOX_DURATION_INPUT_NAME}
          choiceGroupProps={{
            legend: __('Duration of mailbox'),
            layout: 'row',
          }}
        >
          {durationConfig.values.map(mbDuration => (
            <ChoiceField
              key={mbDuration}
              name={MAILBOX_DURATION_INPUT_NAME}
              value={String(mbDuration)}
              label={toLabel[durationConfig.unit][String(mbDuration)]}
            />
          ))}
        </ChoiceFieldGroup>
        {duration && (
          <Base tagName="p" marginTop="small" marginBottom="none">
            {durationConfig.unit === 'm' &&
            duration === MAILBOX_DURATION_ONE_MONTH
              ? __('Pay each month. Saving a credit card is mandatory.')
              : __('Pay each year. No specific payment method required.')}{' '}
            {__('Cancel the subscription whenever you want.')}
          </Base>
        )}
      </Block>
    ) : (
      <MailboxLegacyExpiration />
    ))
  );
}
